import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { TrendingUp, Eye, EyeOff, Gift } from 'lucide-react';

export default function Register() {
  const { referralCode: urlCode } = useParams();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', referralCode: urlCode || '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await register(form);
      toast.success('Account created! Please pay setup fee to activate.');
      navigate('/setup-fee');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">UniqueAiTarde</h1>
            <p className="text-primary-400 text-xs font-medium tracking-wider">MT5 AUTOMATED TRADING</p>
          </div>
        </div>

        <div className="bg-dark-700 border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-white mb-1">Create Account</h2>
          <p className="text-gray-400 text-sm mb-6">Join our trading community today</p>

          {urlCode && (
            <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-3 mb-5 flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary-400 flex-shrink-0" />
              <p className="text-primary-300 text-sm">Referred by code: <strong>{urlCode}</strong></p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 transition-colors placeholder-gray-600"
                  placeholder="John Doe" required />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 transition-colors placeholder-gray-600"
                  placeholder="you@example.com" required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone</label>
                <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 transition-colors placeholder-gray-600"
                  placeholder="+91 XXXXX" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Referral Code</label>
                <input type="text" value={form.referralCode} onChange={e => setForm({ ...form, referralCode: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 transition-colors placeholder-gray-600 uppercase"
                  placeholder="Optional" />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-primary-500 transition-colors placeholder-gray-600"
                    placeholder="Minimum 6 characters" required />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-gray-400 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">Sign in</Link>
          </p>
        </div>

        <footer className="mt-6 text-center text-m text-gray-400">
          Powered by <a href="https://tecai.in" target="_blank" rel="noreferrer" className="text-primary-400 hover:text-primary-300">Tecai</a>
        </footer>
      </div>
    </div>
  );
}
